import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import { Input, Textarea } from 'components/FormFields';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';
import Toaster from 'components/Toaster';
import {
  Container,
  CardHeader,
  CardHeaderRow,
  CardHeaderContent,
  HeaderButtonWrapper,
  CardIcon,
  CardTitleGroup,
  CardTitle,
  CardSubtitle,
  FormRow,
  FieldWrapper,
  LoadingContainer,
  ImagesGrid,
  ImageFieldWrapper,
  ImageLabel,
  FAQList,
  FAQItem,
  FAQToolbar,
  FAQDragHandle,
  FAQContent,
  FAQActions,
  FAQDeleteButton,
  FAQQuestionPreview,
  FAQExpandButton,
  EmptyState,
  ToggleRow,
  ToggleInfo,
  ToggleIcon,
  ToggleText,
  ToggleLabel,
  ToggleDescription,
  Toggle,
  ToggleInput,
  ToggleSlider,
} from './StyledComponents';

// Icons
const ImageIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const ShareIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const LinkIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const HelpCircleIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const GripVerticalIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    stroke="none"
  >
    <circle cx="9" cy="5" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="9" cy="19" r="1.5" />
    <circle cx="15" cy="5" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="15" cy="19" r="1.5" />
  </svg>
);

const ChevronDownIcon = ({ expanded }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.2s ease',
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const BadgeIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// Check if we're on TicketHoss platform
const isTicketHossPlatform = process.env.REACT_APP_PLATFORM === 'tickethoss';

// Image upload field configurations
const IMAGE_FIELDS = [
  { key: 'image', idKey: 'image_id', label: 'Event Image' },
  { key: 'logo', idKey: 'logo_id', label: 'Event Logo' },
  { key: 'promo_image', idKey: 'promo_image_id', label: 'Promo Image' },
  {
    key: 'facebook_image',
    idKey: 'facebook_image_id',
    label: 'Facebook Image',
  },
  {
    key: 'armband_image',
    idKey: 'armband_logo_id',
    label: 'Kiosk Armband Logo',
  },
];

// Helper to convert single image URL to attachments array format for AttachmentUpload
const urlToAttachments = (url, id, name) => {
  if (!url) return [];
  return [
    {
      id: id || 'existing',
      name: name || 'image',
      size: 0,
      sizeFormatted: '',
      url: url,
      type: 'image/jpeg',
      isImage: true,
    },
  ];
};

const MediaMarketingTab = ({ settings = {}, onSettingsChange, loading }) => {
  const [toaster, setToaster] = useState({ isOpen: false });
  const [expandedFAQs, setExpandedFAQs] = useState({});

  const toggleFAQExpanded = useCallback((faqId) => {
    setExpandedFAQs((prev) => ({ ...prev, [faqId]: !prev[faqId] }));
  }, []);

  // Handle field changes
  const handleChange = useCallback(
    (field) => (e) => {
      const value = e?.target?.value ?? e;
      onSettingsChange({
        ...settings,
        [field]: value,
      });
    },
    [settings, onSettingsChange]
  );

  // Handle image attachment change (for AttachmentUpload component)
  const handleImageAttachmentChange = useCallback(
    (urlKey, idKey) => (attachments) => {
      if (attachments.length > 0) {
        onSettingsChange({
          ...settings,
          [urlKey]: attachments[0].url,
          [idKey]: attachments[0].id,
        });
      } else {
        onSettingsChange({
          ...settings,
          [urlKey]: null,
          [idKey]: null,
        });
      }
    },
    [settings, onSettingsChange]
  );

  // Handle toggle changes
  const handleToggleChange = useCallback(
    (field) => (e) => {
      onSettingsChange({
        ...settings,
        [field]: e.target.checked,
      });
    },
    [settings, onSettingsChange]
  );

  // FAQ handlers
  const handleAddFAQ = useCallback(() => {
    const faqs = settings.faqs || [];
    const maxOrder = faqs.reduce(
      (max, faq) => Math.max(max, faq.order || 0),
      0
    );
    const newId = `faq-${Date.now()}`;
    setExpandedFAQs((prev) => ({ ...prev, [newId]: true }));
    onSettingsChange({
      ...settings,
      faqs: [
        ...faqs,
        {
          id: newId,
          question: '',
          answer: '',
          order: maxOrder + 1,
        },
      ],
    });
  }, [settings, onSettingsChange]);

  const handleFAQChange = useCallback(
    (index, field) => (e) => {
      const value = e?.target?.value ?? e;
      const faqs = [...(settings.faqs || [])];
      faqs[index] = { ...faqs[index], [field]: value };
      onSettingsChange({
        ...settings,
        faqs,
      });
    },
    [settings, onSettingsChange]
  );

  const handleFAQDelete = useCallback(
    (index) => () => {
      const faqs = [...(settings.faqs || [])];
      faqs.splice(index, 1);
      onSettingsChange({
        ...settings,
        faqs,
      });
    },
    [settings, onSettingsChange]
  );

  const handleFAQReorder = useCallback(
    (newOrder) => {
      // Update order values based on new positions
      const reorderedFaqs = newOrder.map((faq, idx) => ({
        ...faq,
        order: idx + 1,
      }));
      onSettingsChange({
        ...settings,
        faqs: reorderedFaqs,
      });
    },
    [settings, onSettingsChange]
  );

  if (loading) {
    return (
      <Card>
        <LoadingContainer>Loading media settings...</LoadingContainer>
      </Card>
    );
  }

  const faqs = settings.faqs || [];

  return (
    <Container>
      {/* Event Images Card */}
      <Card>
        <CardHeader>
          <CardIcon>
            <ImageIcon />
          </CardIcon>
          <CardTitleGroup>
            <CardTitle>Event Images</CardTitle>
            <CardSubtitle>
              Upload images displayed across the platform
            </CardSubtitle>
          </CardTitleGroup>
        </CardHeader>

        <ImagesGrid>
          {IMAGE_FIELDS.map((field) => (
            <ImageFieldWrapper key={field.key}>
              <ImageLabel>{field.label}</ImageLabel>
              <AttachmentUpload
                attachments={urlToAttachments(
                  settings[field.key],
                  settings[field.idKey],
                  field.label
                )}
                onAttachmentsChange={handleImageAttachmentChange(
                  field.key,
                  field.idKey
                )}
                maxFiles={1}
                defaultExpanded={!!settings[field.key]}
              />
            </ImageFieldWrapper>
          ))}
        </ImagesGrid>
      </Card>

      {/* Social Media Links Card */}
      <Card>
        <CardHeader>
          <CardIcon>
            <ShareIcon />
          </CardIcon>
          <CardTitleGroup>
            <CardTitle>Social Media Links</CardTitle>
            <CardSubtitle>
              Add your event&apos;s social media profiles
            </CardSubtitle>
          </CardTitleGroup>
        </CardHeader>

        <FormRow columns="1fr 1fr 1fr">
          <FieldWrapper noMargin>
            <Input
              label="Facebook URL"
              type="url"
              value={settings.facebook || ''}
              onChange={handleChange('facebook')}
              placeholder="https://facebook.com/..."
            />
          </FieldWrapper>
          <FieldWrapper noMargin>
            <Input
              label="Instagram URL"
              type="url"
              value={settings.instagram || ''}
              onChange={handleChange('instagram')}
              placeholder="https://instagram.com/..."
            />
          </FieldWrapper>
          <FieldWrapper noMargin>
            <Input
              label="Twitter URL"
              type="url"
              value={settings.twitter || ''}
              onChange={handleChange('twitter')}
              placeholder="https://twitter.com/..."
            />
          </FieldWrapper>
        </FormRow>
      </Card>

      {/* FAQs Card */}
      <Card>
        <CardHeaderRow>
          <CardHeaderContent>
            <CardIcon>
              <HelpCircleIcon />
            </CardIcon>
            <CardTitleGroup>
              <CardTitle>Frequently Asked Questions</CardTitle>
              <CardSubtitle>
                Common questions and answers for event attendees
              </CardSubtitle>
            </CardTitleGroup>
          </CardHeaderContent>
          <HeaderButtonWrapper>
            <ButtonV2 type="button" elevated onClick={handleAddFAQ}>
              + Add FAQ
            </ButtonV2>
          </HeaderButtonWrapper>
        </CardHeaderRow>

        {faqs.length === 0 ? (
          <EmptyState>
            No FAQs added yet. Click the button above to add your first FAQ.
          </EmptyState>
        ) : (
          <FAQList>
            <ReactSortable
              list={faqs}
              setList={handleFAQReorder}
              handle=".faq-drag-handle"
              animation={200}
            >
              {faqs.map((faq, index) => {
                const faqKey = faq.id || index;
                const isExpanded = !!expandedFAQs[faqKey];
                return (
                  <FAQItem key={faqKey}>
                    <FAQToolbar>
                      <FAQDragHandle className="faq-drag-handle">
                        <GripVerticalIcon />
                      </FAQDragHandle>
                      <FAQQuestionPreview
                        onClick={() => toggleFAQExpanded(faqKey)}
                      >
                        {faq.question || 'New Question'}
                      </FAQQuestionPreview>
                      <FAQActions>
                        <FAQExpandButton
                          type="button"
                          onClick={() => toggleFAQExpanded(faqKey)}
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          <ChevronDownIcon expanded={isExpanded} />
                        </FAQExpandButton>
                        <FAQDeleteButton
                          type="button"
                          onClick={handleFAQDelete(index)}
                          title="Delete FAQ"
                        >
                          <TrashIcon />
                        </FAQDeleteButton>
                      </FAQActions>
                    </FAQToolbar>
                    {isExpanded && (
                      <FAQContent>
                        <FieldWrapper>
                          <Input
                            label="Question"
                            value={faq.question || ''}
                            onChange={handleFAQChange(index, 'question')}
                            placeholder="Enter question..."
                          />
                        </FieldWrapper>
                        <FieldWrapper noMargin>
                          <Textarea
                            label="Answer"
                            value={faq.answer || ''}
                            onChange={handleFAQChange(index, 'answer')}
                            placeholder="Enter answer..."
                            rows={2}
                          />
                        </FieldWrapper>
                      </FAQContent>
                    )}
                  </FAQItem>
                );
              })}
            </ReactSortable>
          </FAQList>
        )}
      </Card>

      {/* Content & Links Card */}
      <Card>
        <CardHeader>
          <CardIcon>
            <LinkIcon />
          </CardIcon>
          <CardTitleGroup>
            <CardTitle>Content & Links</CardTitle>
            <CardSubtitle>Event URLs and video content</CardSubtitle>
          </CardTitleGroup>
        </CardHeader>

        <FormRow>
          <FieldWrapper noMargin>
            <Input
              label="Event URL"
              type="url"
              value={settings.url || ''}
              onChange={handleChange('url')}
              placeholder="https://..."
            />
          </FieldWrapper>
          <FieldWrapper noMargin>
            <Input
              label="Info Video URL"
              type="url"
              value={settings.info_video_url || ''}
              onChange={handleChange('info_video_url')}
              placeholder="https://youtube.com/... or https://vimeo.com/..."
            />
          </FieldWrapper>
        </FormRow>
      </Card>

      {/* Cross-Promotion Card */}
      <Card>
        <CardHeader>
          <CardIcon>
            <BadgeIcon />
          </CardIcon>
          <CardTitleGroup>
            <CardTitle>Cross-Promotion</CardTitle>
            <CardSubtitle>
              Show a badge and deep link to let fans discover passes on our
              other platform
            </CardSubtitle>
          </CardTitleGroup>
        </CardHeader>

        <ToggleRow>
          <ToggleInfo>
            <ToggleIcon>
              <BadgeIcon />
            </ToggleIcon>
            <ToggleText>
              {isTicketHossPlatform ? (
                <>
                  <ToggleLabel>Promote Pit Pay Pit Passes</ToggleLabel>
                  <ToggleDescription>
                    Display a badge and deep link on the event page letting fans
                    know that pit passes for drivers and crew are available on
                    Pit Pay
                  </ToggleDescription>
                </>
              ) : (
                <>
                  <ToggleLabel>
                    Promote TicketHoss Grandstand Tickets
                  </ToggleLabel>
                  <ToggleDescription>
                    Display a badge and deep link on the event page letting fans
                    know that grandstand tickets are available on TicketHoss
                  </ToggleDescription>
                </>
              )}
            </ToggleText>
          </ToggleInfo>
          <Toggle>
            <ToggleInput
              type="checkbox"
              checked={
                isTicketHossPlatform
                  ? !!settings.show_pitpay
                  : !!settings.show_tickethoss
              }
              onChange={handleToggleChange(
                isTicketHossPlatform ? 'show_pitpay' : 'show_tickethoss'
              )}
            />
            <ToggleSlider />
          </Toggle>
        </ToggleRow>
      </Card>

      <Toaster
        isOpen={toaster.isOpen}
        onClose={() => setToaster({ isOpen: false })}
        type={toaster.type}
        title={toaster.title}
        message={toaster.message}
        duration={5000}
      />
    </Container>
  );
};

MediaMarketingTab.propTypes = {
  settings: PropTypes.shape({
    image: PropTypes.string,
    image_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    logo: PropTypes.string,
    logo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    promo_image: PropTypes.string,
    promo_image_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    facebook_image: PropTypes.string,
    facebook_image_id: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]),
    armband_image: PropTypes.string,
    armband_logo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    twitter: PropTypes.string,
    url: PropTypes.string,
    info_video_url: PropTypes.string,
    faqs: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        question: PropTypes.string,
        answer: PropTypes.string,
        order: PropTypes.number,
      })
    ),
    show_pitpay: PropTypes.bool,
    show_tickethoss: PropTypes.bool,
  }),
  onSettingsChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default MediaMarketingTab;
